import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Area, Section } from "./fields";
import { Textarea } from "../ui/textarea";
import DictateButton from "../DictateButton";
import { Button } from "../ui/button";

export const emptyGty = { anything_else: "", guided_answers: {} };

export default function GettingToKnowYouForm({ data, onChange, clientId }) {
  const [questions, setQuestions] = useState([]);
  const [customQs, setCustomQs] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const guided = data.guided_answers || {};

  useEffect(() => {
    api.get("/questions?active=true").then((r) => setQuestions(r.data)).catch(() => {});
    if (clientId) {
      api.get(`/clients/${clientId}`).then((r) => {
        if (r.data.suggested_questions?.length) setSuggestions(r.data.suggested_questions);
        setCustomQs(r.data.custom_questions || []);
      }).catch(() => {});
    }
  }, [clientId]);

  const setGuided = (q, answer) =>
    onChange({ ...data, guided_answers: { ...guided, [q.id]: { question: q.text, answer } } });

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data: s } = await api.post(`/clients/${clientId}/suggest-questions`, {}, { timeout: 120000 });
      setSuggestions(s);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSuggesting(false);
    }
  };

  const accept = async (sid) => {
    try {
      await api.post(`/clients/${clientId}/suggest-questions/${sid}/accept`);
      setSuggestions((s) => s.map((x) => (x.id === sid ? { ...x, added: true } : x)));
      const { data: c } = await api.get(`/clients/${clientId}`);
      setCustomQs(c.custom_questions || []);
      toast.success("Added to this client's questions only — not the shared library");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="gty-form">
      <div className="rounded-lg border border-stone-200 bg-accent p-4 text-sm text-stone-600" data-testid="gty-note">
        Person-centered conversation starters from the Question Bank. Ask what fits, skip the rest — answers feed the AI care plan documents.
      </div>

      <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="guided-questions-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-semibold text-stone-900">Guided Questions</h3>
            <p className="mt-0.5 text-xs text-stone-500">Manage the library anytime under Questions in the top menu.</p>
          </div>
          <Button variant="outline" size="sm" onClick={suggest} disabled={suggesting || !clientId}
            className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="suggest-questions-button">
            {suggesting ? "Thinking..." : "Suggest Questions for This Client (AI)"}
          </Button>
        </div>
        {questions.length > 0 && (
          <div className="mt-4 space-y-4">
            {questions.map((q) => (
              <div key={q.id}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-stone-800" data-testid={`guided-q-${q.id}`}>{q.text}</p>
                  <DictateButton testid={`guided-dictate-${q.id}`} onText={(t) => setGuided(q, (guided[q.id]?.answer ? guided[q.id].answer + " " : "") + t)} />
                </div>
                <Textarea rows={2} value={guided[q.id]?.answer || ""} onChange={(e) => setGuided(q, e.target.value)}
                  className="mt-1.5 border-stone-300 bg-white text-sm" data-testid={`guided-a-${q.id}`} />
              </div>
            ))}
          </div>
        )}
        {customQs.length > 0 && (
          <div className="mt-4 space-y-4 border-t border-stone-200 pt-4" data-testid="custom-questions-section">
            <p className="text-xs font-medium uppercase tracking-wide text-stone-400">Questions for this client only</p>
            {customQs.map((q) => (
              <div key={q.id}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-stone-800" data-testid={`custom-q-${q.id}`}>{q.text}</p>
                  <DictateButton testid={`custom-dictate-${q.id}`} onText={(t) => setGuided(q, (guided[q.id]?.answer ? guided[q.id].answer + " " : "") + t)} />
                </div>
                <Textarea rows={2} value={guided[q.id]?.answer || ""} onChange={(e) => setGuided(q, e.target.value)}
                  className="mt-1.5 border-stone-300 bg-white text-sm" data-testid={`custom-a-${q.id}`} />
              </div>
            ))}
          </div>
        )}
        {suggestions && suggestions.length > 0 && (
          <div className="mt-4 space-y-2 border-t border-stone-200 pt-4" data-testid="suggestions-list">
            <p className="text-xs font-medium uppercase tracking-wide text-stone-400">AI suggestions for this client</p>
            {suggestions.map((s) => (
              <div key={s.id} className="flex items-start gap-3 rounded-lg border border-stone-200 bg-accent/50 p-3" data-testid={`suggestion-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-stone-800">{s.question}</p>
                  <p className="mt-0.5 text-xs text-stone-500">{s.reason}{s.theme ? ` · ${s.theme}` : ""}</p>
                </div>
                <Button variant="outline" size="sm" disabled={s.added} onClick={() => accept(s.id)}
                  className="shrink-0 border-stone-300 text-stone-700 hover:bg-primary hover:text-white" data-testid={`suggestion-add-${s.id}`}>
                  {s.added ? "Added" : "Add for This Client"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Section title="Anything Else">
        <div className="md:col-span-2">
          <Area label="Anything else we should know about them as a person?" tid="gty-anything-else"
            value={data.anything_else} onChange={(e) => onChange({ ...data, anything_else: e.target.value })} />
        </div>
      </Section>
    </div>
  );
}
